import React, { useEffect, useRef, useState } from "react";

import Image from "next/image";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  List,
  ListItemButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
  useMediaQuery,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

import { Deckplan, UniqueShip } from "@/interfaces/IUniqueShip";
import { toKebabCase, toTitleCase } from "@/utils/helpers";

import ArrowNextIcon from "@mui/icons-material/ArrowForwardRounded";
import ArrowPrevIcon from "@mui/icons-material/ArrowBackRounded";

const DeckPlans: React.FC<{
  data?: Deckplan[];
  name?: string;
  selectedTab?: string;
  shipData?: UniqueShip;
}> = ({ data }) => {
  const [selectedDeck, setSelectedDeck] = useState<string>(
    `${toKebabCase(data![0]?.name ?? "")}_1`,
  );

  // DECK PLANS functionality
  const mainRef = useRef<HTMLDivElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [thumbTop, setThumbTop] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const previewTrackHeight = 200;
  const thumbHeight = 50;

  // When the main container scrolls, updates the thumb position
  useEffect(() => {
    const container = mainRef.current;
    const updateThumbPosition = () => {
      if (!container) return;
      const scrollTop = container.scrollTop;
      const maxScrollTop = container.scrollHeight - container.clientHeight;
      const scrollRatio = scrollTop / maxScrollTop;

      const maxThumbTop = previewTrackHeight - thumbHeight;
      const newThumbTop = scrollRatio * maxThumbTop;
      setThumbTop(newThumbTop);
    };

    if (container) {
      container.addEventListener("scroll", updateThumbPosition);
    }

    return () => {
      if (container) {
        container.removeEventListener("scroll", updateThumbPosition);
      }
    };
  }, []);

  // Scroll the main container to 30% from the top
  useEffect(() => {
    const container = mainRef.current;
    if (container) {
      const scrollHeight = container.scrollHeight - container.clientHeight;
      const targetScrollTop = scrollHeight * 0.3; // 30% scroll
      container.scrollTop = targetScrollTop;
    }
  }, []);

  // When dragging the thumb, scroll the main container
  useEffect(() => {
    // Measured once per drag gesture (the track's position is static while dragging) instead
    // of on every mousemove/touchmove, which forces a synchronous layout reflow each call.
    const trackTop = isDragging ? (trackRef.current?.getBoundingClientRect().top ?? 0) : 0;

    const handleDragging = (e: MouseEvent | TouchEvent) => {
      if (!isDragging || !mainRef.current || !trackRef.current) return;

      // Support both mouse and touch events
      const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;
      const mouseY = clientY - trackTop;

      const maxThumbTop = previewTrackHeight - thumbHeight;
      const clampedY = Math.max(0, Math.min(mouseY - thumbHeight / 2, maxThumbTop));
      setThumbTop(clampedY);

      const scrollRatio = clampedY / maxThumbTop;
      const maxScrollTop = mainRef.current.scrollHeight - mainRef.current.clientHeight;
      mainRef.current.scrollTop = scrollRatio * maxScrollTop;
    };

    const handleDragEnd = () => {
      setIsDragging(false);
      document.removeEventListener("mousemove", handleDragging as EventListener);
      document.removeEventListener("mouseup", handleDragEnd);
      document.removeEventListener("touchmove", handleDragging as EventListener);
      document.removeEventListener("touchend", handleDragEnd);
    };

    if (isDragging) {
      document.addEventListener("mousemove", handleDragging as EventListener);
      document.addEventListener("mouseup", handleDragEnd);
      document.addEventListener("touchmove", handleDragging as EventListener, { passive: false });
      document.addEventListener("touchend", handleDragEnd);
    }

    return () => {
      document.removeEventListener("mousemove", handleDragging as EventListener);
      document.removeEventListener("mouseup", handleDragEnd);
      document.removeEventListener("touchmove", handleDragging as EventListener);
      document.removeEventListener("touchend", handleDragEnd);
    };
  }, [isDragging]);

  const [imageLoading, setImageLoading] = useState(true);

  const isMobile = useMediaQuery("(max-width: 900px)");

  return (
    <>
      <Grid container size={{ xs: 12 }} spacing={2}>
        {(data?.length ?? 0) > 1 && (
          <Grid size={{ xs: 12, md: 3, lg: 2.5 }}>
            {isMobile ? (
              <Box
                sx={{
                  mt: { xs: "1rem", md: "0px" },
                  display: { xs: "grid", md: "none" },
                  gap: "12px",
                  alignItems: "center",
                  gridTemplateColumns: "56px auto 56px",
                  pb: "14px",
                  borderBottom: "1px solid #ccc",
                }}
              >
                <Button
                  aria-label="Previous deck"
                  onClick={() => {
                    if (data && data.length > 0) {
                      const currentIndex = data.findIndex(
                        (item, index) =>
                          `${toKebabCase(item?.name ?? "")}_${index + 1}` === selectedDeck,
                      );

                      if (currentIndex > 0) {
                        const prevItem = data[currentIndex - 1];
                        setSelectedDeck(`${toKebabCase(prevItem?.name ?? "")}_${currentIndex}`);
                        setImageLoading(true);
                      }
                    }
                  }}
                  disabled={
                    data?.findIndex(
                      (item, index) =>
                        `${toKebabCase(item?.name ?? "")}_${index + 1}` === selectedDeck,
                    ) === 0
                  }
                  sx={{
                    minWidth: "unset",
                    height: "40px",
                  }}
                >
                  <ArrowPrevIcon />
                </Button>
                <Box sx={{ width: "100%" }}>
                  <FormControl focused sx={{ width: "100%" }}>
                    <InputLabel>Choose a Deck</InputLabel>
                    <Select
                      value={selectedDeck}
                      label="Choose a Deck"
                      onChange={(event: SelectChangeEvent<typeof selectedDeck>) => {
                        setSelectedDeck(event.target.value);
                        setImageLoading(true);
                      }}
                      fullWidth
                      size="small"
                    >
                      {data?.map((item, deckIndex) => (
                        <MenuItem
                          key={deckIndex}
                          value={`${toKebabCase(item?.name ?? "")}_${deckIndex + 1}`}
                        >
                          {item?.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Button
                  aria-label="Next deck"
                  onClick={() => {
                    if (data && data.length > 0) {
                      const currentIndex = data.findIndex(
                        (item, index) =>
                          `${toKebabCase(item?.name ?? "")}_${index + 1}` === selectedDeck,
                      );
                      if (currentIndex < data.length - 1) {
                        const nextItem = data[currentIndex + 1];
                        setSelectedDeck(`${toKebabCase(nextItem?.name ?? "")}_${currentIndex + 2}`);
                        setImageLoading(true);
                      }
                    }
                  }}
                  disabled={
                    data?.findIndex(
                      (item, index) =>
                        `${toKebabCase(item?.name ?? "")}_${index + 1}` === selectedDeck,
                    ) ===
                    (data?.length ?? 1) - 1
                  }
                  sx={{
                    minWidth: "unset",
                    height: "40px",
                  }}
                >
                  <ArrowNextIcon />
                </Button>
              </Box>
            ) : (
              <Box
                sx={{
                  display: { xs: "none", md: "block" },
                  position: "sticky",
                  top: "185px",
                  height: "calc(100vh - 310px)",
                  overflow: "auto",
                }}
              >
                <List component="div" disablePadding>
                  {data?.map((item, deckIndex) => (
                    <ListItemButton
                      key={deckIndex}
                      sx={{
                        mb: "8px",
                        display: "flex",
                        justifyContent: "space-between",
                        borderRadius: "8px",
                        fontFamily: "'blauerNue', 'blauerNue Fallback'",
                        p: "8px 10px 8px 16px",
                        bgcolor: "#f0f0f0",
                        color: "#1f1f1f",
                        border: "2px solid #f3f3f3",
                        transition: "all 0.3s",
                        "&:hover": {
                          bgcolor: "#f3f3f3",
                          color: "#8F0034",
                          border: "2px solid #8F003480",
                        },
                        ...(selectedDeck ===
                          `${toKebabCase(item?.name ?? "")}_${deckIndex + 1}` && {
                          bgcolor: "#e30053", //3000ff
                          color: "#fff",
                          border: "2px solid #e30053",
                          "&:hover": {
                            bgcolor: "#e30053",
                            color: "#fff",
                            border: "2px solid #8F0034",
                          },
                        }),
                      }}
                      onClick={() => {
                        setSelectedDeck(`${toKebabCase(item?.name ?? "")}_${deckIndex + 1}`);
                        setImageLoading(true);
                      }}
                    >
                      <Box component="span">{toTitleCase(item?.name ?? "") ?? ""}</Box>
                      <Box
                        sx={{
                          display: "grid",
                          placeItems: "center",
                          color: "#fff",
                          borderRadius: "16px",
                          height: "30px",
                          svg: {
                            width: "30px",
                            height: "30px",
                          },
                        }}
                      >
                        {selectedDeck === `${toKebabCase(item?.name ?? "")}_${deckIndex + 1}` ? (
                          <ArrowNextIcon />
                        ) : (
                          <></>
                        )}
                      </Box>
                    </ListItemButton>
                  ))}
                </List>
              </Box>
            )}
          </Grid>
        )}

        <Grid size={{ xs: 12, md: 9, lg: 9.5 }} spacing={2}>
          {data
            ?.filter(
              (item, index) => `${toKebabCase(item?.name ?? "")}_${index + 1}` === selectedDeck,
            )
            ?.map((deck, deckIndex) => (
              <Grid
                container
                size={{ xs: 12 }}
                spacing={2}
                key={deckIndex}
                id={`${toKebabCase(deck?.name ?? "")}`}
              >
                <Grid size={{ xs: 12, md: 7, lg: 6 }} sx={{ position: "relative" }}>
                  <Box sx={{ position: "relative" }}>
                    <Box
                      ref={mainRef}
                      sx={{
                        minHeight: { xs: "300px", md: "auto" },
                        height: { xs: "55vh", md: "calc(100dvh - 310px)" },
                        overflow: "auto",
                        overflowX: "hidden",
                        border: "3px solid #060273",
                        borderRadius: "10px",
                        p: "10px",
                      }}
                    >
                      {(deck?.images?.filter((x) => x.href != "").length ?? 0) > 0 && (
                        <Box
                          id="deck"
                          sx={{
                            width: { xs: "100%", sm: "60%", lg: "100%" },
                            float: "left",
                            position: "relative",
                            height: "100%",
                            img: {
                              position: "absolute",
                              top: "0rem",
                              left: {
                                xs: "-2rem",
                                md: "-6rem",
                                lg: "-3rem",
                                xl: "0rem",
                              },
                              transform: "rotate(-90deg) translate(-100%, 0%) scale(1)",
                              transformOrigin: "top left",
                              height: { xs: "100vw", md: "550px" },
                              width: { xs: "200vh", lg: "250%" },
                            },
                          }}
                        >
                          <Image
                            src={deck?.images![0]?.href ?? ""}
                            alt={deck?.images![0]?.name ?? ""}
                            width={900}
                            height={400}
                            unoptimized
                            loader={({ src }) => src}
                            style={{ objectFit: "contain" }}
                            onLoad={() => setImageLoading(false)}
                          />

                          <Box
                            sx={{
                              position: "absolute",
                              top: "50%",
                              left: "50%",
                              transform: "translateY(-50%)",
                            }}
                          ></Box>
                        </Box>
                      )}
                    </Box>

                    {imageLoading && (
                      <Box
                        sx={{
                          display: "grid",
                          placeItems: "center",
                          position: "absolute",
                          top: "0px",
                          width: "100%",
                          height: "100%",
                          zIndex: 10,
                          bgcolor: "#fff4",
                          backdropFilter: "blur(15px)",
                          borderRadius: "10px",
                          border: "2px solid #060273",
                        }}
                      >
                        <Box
                          sx={{
                            mt: "1rem",
                            fontFamily: "'blauerNue', 'blauerNue Fallback'",
                            color: "#060273",
                            textShadow: "0px 0px 4px #fff, 0px 0px 14px #fff",
                            fontWeight: 600,
                            fontSize: { xs: "18px", md: "20px" },
                          }}
                        >
                          Loading Deck Image...!
                        </Box>
                      </Box>
                    )}

                    <Box
                      sx={{
                        position: "absolute",
                        top: "10px",
                        right: "10px",
                        width: "fit-content",
                      }}
                    >
                      <Box
                        sx={{
                          border: "1px solid #ccc",
                          boxShadow: "0px 0px 10px rgba(0,0,0,0.2)",
                          height: "fit-content",
                          p: "6px",
                          borderRadius: "10px",
                          width: "fit-content",
                        }}
                      >
                        <Box
                          ref={trackRef}
                          sx={{
                            height: `${previewTrackHeight}px`,
                            width: "40px",
                            position: "relative",
                            borderRadius: "8px",
                            boxSizing: "border-box",
                            img: {
                              p: "18px 6px",
                            },
                          }}
                        >
                          <Box
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setIsDragging(true);
                            }}
                            sx={{
                              width: "40px",
                              bgcolor: "#222",
                              position: "absolute",
                              top: `${thumbTop}px`,
                              height: `${thumbHeight}px`,
                              backgroundColor: "#7226FF80",
                              borderRadius: "6px",
                              cursor: "grab",
                              zIndex: 2,
                            }}
                          ></Box>

                          <Image
                            src="/images/others/cruise-ship-v2.png"
                            alt=""
                            width={40}
                            height={200}
                            loader={({ src }) => src}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, md: 5, lg: 6 }}>
                  <Box
                    sx={{
                      pl: { xs: "0px", md: "1rem" },
                      pt: { xs: "10px", md: "0rem" },
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 700,
                        fontSize: { xs: "1.5rem", md: "22px" },
                        color: "#060273",
                        textAlign: "left",
                        mt: { xs: "1rem", md: "-8px" },
                      }}
                    >
                      Features on this Deck
                    </Typography>

                    <Box
                      id={"content"}
                      sx={{
                        position: "relative",
                        display: "block",
                        ul: {
                          mt: "5px",
                          display: "inline-flex",
                          flexWrap: "wrap",
                          gap: "0px 6px",
                          li: {
                            mb: "7px",
                            listStyle: "none",
                            fontSize: "14px",
                            display: "inline",
                            bgcolor: "#f5f5f5",
                            border: "1px solid #aaa",
                            p: "7px 13px 7px",
                            borderRadius: "16px",
                            lineHeight: 1.2,
                          },
                        },
                        "div > p": {
                          "&:not(p:first-of-type)": {
                            pt: "1rem",
                          },
                          b: {
                            fontSize: "1.1rem",
                          },
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: "100%",
                          lineHeight: "25px",
                        }}
                        dangerouslySetInnerHTML={{
                          __html: deck.description ?? "",
                        }}
                      ></Box>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            ))}
        </Grid>
      </Grid>
    </>
  );
};

export default DeckPlans;
